import { differenceInDays } from 'date-fns'
import { List } from 'immutable'
import { ReactElement } from 'react'

import BonusEditForm, { BonusResult } from '../../components/employees-single/bonus/BonusEditForm'
import CoarseCarAllowanceForm, {
  CoarseCarAllowanceResult,
} from '../../components/employees-single/car-allowance/CoarseCarAllowanceForm'
import DetailedCarAllowanceEditForm, {
  CarAllowanceResult,
} from '../../components/employees-single/car-allowance/DetailedCarAllowanceEditForm'
import ExtraPensionEditForm, {
  OneTimePayPensionResultFields,
} from '../../components/employees-single/extra-pension/ExtraPensionEditForm'
import LeaveEditForm, { Result as LeaveResult } from '../../components/employees-single/leave/LeaveEditForm'
import PayCheckAdvanceEditForm, {
  PayCheckAdvanceResult,
} from '../../components/employees-single/pay-check-advance/PayCheckAdvanceEditForm'
import ReimbursementEditForm, {
  ReimbursementResult,
} from '../../components/employees-single/reimbursement/ReimbursementEditForm'
import SalaryReductionEditForm, {
  SalaryReductionResult,
} from '../../components/employees-single/salary-reduction/SalaryReductionEditForm'
import CoarseSalaryRegistrationForm, {
  ResultFields as CoarseSalaryRegistrationResult,
} from '../../components/employees-single/salary-registrations/CoarseSalaryRegistrationForm'
import DetailedSalaryRegistrationEditForm, {
  ResultFields as DetailedSalaryRegistrationResult,
} from '../../components/employees-single/salary-registrations/DetailedSalaryRegistrationEditForm'
import CoarseTimeRegistrationForm, {
  CoarseTimeRegistrationResult,
} from '../../components/employees-single/time-registration/CoarseTimeRegistrationForm'
import DetailedTimeRegistrationEditForm, {
  ResultFields as TimeRegistrationResult,
} from '../../components/employees-single/time-registration/DetailedTimeRegistrationEditForm'
import TravelAllowanceEditForm, {
  TravelAllowanceResult,
} from '../../components/employees-single/travel-allowance/TravelAllowanceEditForm'
import FeeEditForm, { FeeResult } from '../../components/freelancers-single/fee/FeeEditForm'
import { AccountingAccount } from '../../model/accountingIntegration'
import CoarseSalaryRegistration from '../../model/coarseSalaryRegistration'
import Company from '../../model/company'
import Contract from '../../model/contract'
import ContractDelta from '../../model/contractDelta'
import CostCenter from '../../model/costCenter'
import Department from '../../model/department'
import Employee from '../../model/employee'
import LeaveBalance from '../../model/leaveBalance'
import LeaveType from '../../model/leaveType'
import PensionCompany from '../../model/pensionCompany'
import Project from '../../model/project'
import SalaryCycle from '../../model/salaryCycle'
import SalaryPeriod from '../../model/salaryPeriod'
import SalaryType from '../../model/salaryType'
import SupplementBalance from '../../model/supplementBalance'
import SupplementType from '../../model/supplementType'
import { CarAllowanceReducer } from '../../reducers/carAllowances'
import { CoarseSalaryRegistrationReducer } from '../../reducers/coarseSalaryRegistrations'
import { CoarseTimeRegistrationReducer } from '../../reducers/coarseTimeRegistrations'
import { OneTimePayPensionReducer } from '../../reducers/oneTimePayPensions'
import { OneTimePayReducer } from '../../reducers/oneTimePays'
import { SalaryRegistrationReducer } from '../../reducers/salaryRegistrations'
import { TimeRegistrationReducer } from '../../reducers/timeRegistrations'
import { UserReducer } from '../../reducers/user'
import { buildDate, formatAPIDate, getDate } from '../../utils/date-utils'

export type ResultData =
  | BonusResult
  | ReimbursementResult
  | CarAllowanceResult
  | LeaveResult
  | TravelAllowanceResult
  | OneTimePayPensionResultFields
  | SalaryReductionResult
  | TimeRegistrationResult
  | CoarseTimeRegistrationResult
  | DetailedSalaryRegistrationResult
  | CoarseSalaryRegistrationResult
  | CoarseCarAllowanceResult
  | FeeResult
  | undefined

type Props = {
  employeeID: string
  content: string
  adjustKey: string
  oldValue: ResultData
  period: string[]
  periodContract: ContractDelta | undefined

  chosenSalaryCycle: SalaryCycle | undefined
  chosenSalaryCycleID: string
  chosenPeriod: SalaryPeriod | undefined
  chosenPeriodID: string

  employee: Employee | undefined
  company: Company
  oneTimePays: OneTimePayReducer
  salaryCycle: SalaryCycle | undefined
  supplementBalances: List<SupplementBalance>
  supplementTypes: List<SupplementType>
  leaveBalances: List<LeaveBalance>
  leaveTypes: List<LeaveType>
  carAllowance: CarAllowanceReducer
  costCenters: List<CostCenter>
  user: UserReducer
  departments: List<Department>
  timeRegistrations: TimeRegistrationReducer
  pensionCompanies: List<PensionCompany>
  oneTimePayPensions: OneTimePayPensionReducer
  //employeeContractDeltas: EmployeeContractDeltaReducer
  viewingContract?: Contract
  contractDelta: ContractDelta[]
  coarseTimeRegistrations: CoarseTimeRegistrationReducer
  salaryTypes: List<SalaryType>
  coarseSalaryRegistrations: CoarseSalaryRegistrationReducer
  salaryRegistrations: SalaryRegistrationReducer
  projects: List<Project>

  addAdjustment: (type: string, amount: number, key: string, data: ResultData) => void
}

/**
 * The disposition date for these adjustments is the last day of the period, but the payroll run for a given
 * month is processed before the month ends, so a disposition date of e.g. 27-29 February would be missed
 * by that run. Capping it to the 26th ensures it's always caught by the same month's payroll.
 */
function capFebruaryDispositionDate(date: string): string {
  const parsedDate = getDate(date)
  if (parsedDate.getMonth() === 1) {
    return formatAPIDate(buildDate(parsedDate.getFullYear(), 1, 26))
  }
  return date
}

export default function PayRollAddEmployeePopup(props: Props): ReactElement | null {
  const { content, addAdjustment, adjustKey, oldValue } = props
  let newContent = content

  let cycle: SalaryCycle = {
    id: '',
    frequency: 'Monthly',
    prepaid: false,
    offset: null,
    salaryPeriods: [],
  }
  if (props.salaryCycle) {
    cycle = props.salaryCycle
  }

  const costCenterAccounting = props.company.costCenterAccounting || 'Off'
  const employee = props.employee

  if (
    !employee ||
    !props.viewingContract ||
    !props.salaryCycle ||
    !props.chosenPeriod ||
    !employee.activeContract ||
    !props.periodContract
  )
    return null

  const contract = props.periodContract.contract

  const detailedSalary = contract.salaryRegistrationMethodType === 'Detailed'
  const detailedTime = contract.timeRegistrationMethodType === 'Detailed'
  const detailedCar = contract.carAllowanceRegistrationMethodType === 'Detailed'

  if (detailedSalary && newContent === 'salary') {
    newContent = 'salary-detailed'
  }
  if (detailedTime && newContent === 'time') {
    newContent = 'time-detailed'
  }
  if (detailedCar && newContent === 'car') {
    newContent = 'car-detailed'
  }

  const getCoarseSalaryRegistrations = (): CoarseSalaryRegistration[] => {
    const period = props.chosenPeriod
    if (!period) {
      return []
    }
    return props.coarseSalaryRegistrations.coarseSalaryRegistrations
      .filter((registration) => registration.employeeID === employee.id && registration.salaryPeriodID === period.id)
      .toArray()
  }

  const getCoarseTimeRegistrations = () => {
    if (props.chosenPeriodID === '') {
      return []
    }
    return props.coarseTimeRegistrations.coarseTimeRegistrations.filter(
      (registration) => registration.employeeID === employee.id && registration.salaryPeriodID === props.chosenPeriodID
    )
  }
  const getWorkDays = () => {
    const registrations = getCoarseTimeRegistrations()
    let workDays = 0
    let hasWorkDays = false
    registrations.forEach((registration) => {
      workDays += registration.days
      hasWorkDays = true
    })
    return hasWorkDays ? workDays : undefined
  }
  const getWorkHours = () => {
    const workHours: Record<string, number> = {}
    const registrations = getCoarseTimeRegistrations()
    registrations.forEach((registration) => {
      workHours[registration.salaryTypeID] = registration.hours
    })
    return workHours
  }

  function submitBonus(result: BonusResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitReimb(result: ReimbursementResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitCar(result: CarAllowanceResult) {
    console.log(result)
    let km = 0
    result.trips.forEach((e) => {
      km += e.kilometers
    })
    addAdjustment(newContent, km, adjustKey, result)
  }

  function submitCarDetailed(result: CoarseCarAllowanceResult) {
    addAdjustment(newContent, result.kilometers, adjustKey, result)
  }

  function submitVacation(result: LeaveResult) {
    const days = differenceInDays(getDate(result.endDate), getDate(result.startDate)) + 1
    addAdjustment(newContent, days, adjustKey, result)
  }

  function submitTravel(result: TravelAllowanceResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitAdvance(result: PayCheckAdvanceResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitPension(result: OneTimePayPensionResultFields) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitReduction(result: SalaryReductionResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitFee(result: FeeResult) {
    result.dispositionDate = capFebruaryDispositionDate(props.period[1])
    addAdjustment(newContent, result.amount, adjustKey, result)
  }

  function submitTime(result: CoarseTimeRegistrationResult) {
    let hours = 0
    console.log(result)
    for (const k in result.workHours) {
      hours += result.workHours[k]
    }
    addAdjustment(newContent, hours, adjustKey, result)
  }

  function submitTimeDetailed(result: TimeRegistrationResult) {
    const time = result.hours || 0
    addAdjustment(newContent, time, adjustKey, result)
  }

  function submitDetailSalary(result: DetailedSalaryRegistrationResult) {
    addAdjustment(newContent, result.quantity, adjustKey, result)
  }

  function submitCoarseSalary(result: CoarseSalaryRegistrationResult) {
    let amount = 0
    for (const k in result.registrations) {
      amount += result.registrations[k].quantity * result.registrations[k].rate
    }
    addAdjustment(newContent, amount, adjustKey, result)
  }

  if (newContent === 'bonus') {
    return (
      <div style={{ padding: '30px' }}>
        <BonusEditForm
          editing={true}
          canApproveObjects={false}
          employee={employee}
          company={props.company}
          oneTimePays={props.oneTimePays}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          supplementBalances={props.supplementBalances}
          supplementTypes={props.supplementTypes}
          leaveBalances={props.leaveBalances}
          leaveTypes={props.leaveTypes}
          costCenters={props.costCenters}
          user={props.user}
          noPeriod={true}
          onSubmit={submitBonus}
          prefill={oldValue as BonusResult}
        />
      </div>
    )
  }

  if (newContent === 'reimbursement') {
    return (
      <div style={{ padding: '30px' }}>
        <ReimbursementEditForm
          editing={true}
          canApproveObjects={false}
          employee={employee}
          company={props.company}
          oneTimePays={props.oneTimePays}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          costCenters={props.costCenters}
          noPeriod={true}
          onSubmit={submitReimb}
          prefill={oldValue as ReimbursementResult}
        />
      </div>
    )
  }

  if (newContent === 'car') {
    return (
      <div style={{ padding: '30px' }}>
        <CoarseCarAllowanceForm
          kilometers={oldValue ? (oldValue as CoarseCarAllowanceResult).kilometers : 0}
          onSubmit={submitCarDetailed}
        />
      </div>
    )
  }

  if (newContent === 'car-detailed') {
    return (
      <div style={{ padding: '30px' }}>
        <DetailedCarAllowanceEditForm
          employee={employee}
          company={props.company}
          costCenters={props.costCenters}
          newCarAllowance={false}
          carAllowances={props.carAllowance}
          departments={props.departments}
          dateRange={props.period}
          onSubmit={submitCar}
          prefill={oldValue as CarAllowanceResult}
        />
      </div>
    )
  }

  if (newContent === 'vacation') {
    return (
      <div style={{ padding: '30px' }}>
        <LeaveEditForm
          employee={employee}
          company={props.company}
          timeRegistrations={props.timeRegistrations}
          leaveTypes={props.leaveTypes}
          period={props.period}
          onSubmit={submitVacation}
          prefill={oldValue as LeaveResult}
          dateRange={props.period}
        />
      </div>
    )
  }

  if (newContent === 'travel') {
    return (
      <div style={{ padding: '30px' }}>
        <TravelAllowanceEditForm
          editing={true}
          canApproveObjects={false}
          employee={employee}
          company={props.company}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          costCenters={props.costCenters}
          oneTimePays={props.oneTimePays}
          noPeriod={true}
          prefill={oldValue as TravelAllowanceResult}
          onSubmit={submitTravel}
        />
      </div>
    )
  }

  if (newContent === 'advance') {
    return (
      <div style={{ padding: '30px' }}>
        <PayCheckAdvanceEditForm
          editing={true}
          canApproveObjects={true}
          employee={employee}
          company={props.company}
          oneTimePays={props.oneTimePays}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          costCenters={props.costCenters}
          noPeriod={true}
          prefill={oldValue as PayCheckAdvanceResult}
          onSubmit={submitAdvance}
        />
      </div>
    )
  }

  if (newContent === 'extra-pension') {
    return (
      <div style={{ padding: '30px' }}>
        <ExtraPensionEditForm
          editing={true}
          canApproveObjects={true}
          company={props.company}
          employee={employee}
          salaryCycle={cycle}
          oneTimePayID={undefined}
          oneTimePayPensions={props.oneTimePayPensions}
          pensionCompanies={props.pensionCompanies}
          viewingContract={props.viewingContract}
          noPeriod={true}
          onSubmit={submitPension}
          prefill={oldValue as OneTimePayPensionResultFields}
        />
      </div>
    )
  }

  if (newContent === 'salary-reduction') {
    return (
      <div style={{ padding: '30px' }}>
        <SalaryReductionEditForm
          editing={true}
          canApproveObjects={true}
          employee={employee}
          isFreelancer={employee.affiliationType === 'Freelancer'}
          company={props.company}
          oneTimePayID={undefined}
          oneTimePays={props.oneTimePays}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          costCenters={props.costCenters}
          noPeriod={true}
          prefill={oldValue as SalaryReductionResult}
          onSubmit={submitReduction}
        />
      </div>
    )
  }

  if (newContent === 'fee') {
    return (
      <div style={{ padding: '30px' }}>
        <FeeEditForm
          editing={true}
          canApproveObjects={true}
          employee={employee}
          company={props.company}
          oneTimePays={props.oneTimePays}
          salaryCycle={cycle}
          costCenterAccounting={costCenterAccounting}
          costCenters={props.costCenters}
          accounts={List<AccountingAccount>()}
          noPeriod={true}
          prefill={oldValue as FeeResult}
          onSubmit={submitFee}
        />
      </div>
    )
  }

  if (newContent === 'time') {
    return (
      <div style={{ padding: '30px' }}>
        <CoarseTimeRegistrationForm
          employee={employee}
          noPeriod={true}
          onSubmit={submitTime}
          workSchedule={contract.workSchedule}
          selectedContractID={props.viewingContract.id}
          loadedDelta={props.contractDelta}
          workHours={getWorkHours()}
          workDays={getWorkDays()}
          period={props.chosenPeriod}
          addAlert={function () {
            return
          }}
          salaryTypes={props.salaryTypes}
          prefill={oldValue as CoarseTimeRegistrationResult}
        />
      </div>
    )
  }

  if (newContent === 'time-detailed') {
    return (
      <div style={{ padding: '30px' }}>
        <DetailedTimeRegistrationEditForm
          company={props.company}
          employee={employee}
          timeRegistrations={props.timeRegistrations}
          costCenters={props.costCenters}
          departments={props.departments}
          mode="Hours"
          projects={props.projects}
          salaryTypes={props.salaryTypes}
          includeDateRange={[new Date(props.period[0]), new Date(props.period[1])]}
          onSubmit={submitTimeDetailed}
        />
      </div>
    )
  }

  if (newContent === 'salary') {
    return (
      <div style={{ padding: '30px' }}>
        <CoarseSalaryRegistrationForm
          employee={employee}
          contract={props.periodContract.contract}
          period={props.chosenPeriod}
          salaryTypes={props.salaryTypes}
          coarseSalaryRegistrations={getCoarseSalaryRegistrations()}
          addAlert={function () {
            return
          }}
          prefill={oldValue as CoarseSalaryRegistrationResult}
          onSubmit={submitCoarseSalary}
          fixedRate={true}
        />
      </div>
    )
  }

  if (newContent === 'salary-detailed') {
    return (
      <div style={{ padding: '30px' }}>
        <DetailedSalaryRegistrationEditForm
          company={props.company}
          costCenters={props.costCenters}
          departments={props.departments}
          employee={employee}
          salaryTypes={props.salaryTypes}
          salaryRegistrations={props.salaryRegistrations}
          dateRange={props.period}
          prefill={oldValue as DetailedSalaryRegistrationResult}
          onSubmit={submitDetailSalary}
        />
      </div>
    )
  }
  return null
}
